import { CheckCircleOutlined } from '@ant-design/icons'
import { Alert, Radio, Tag } from 'antd'
import { RadioChangeEvent } from 'antd/lib/radio'
import { HTS_TYPE } from '@Orders/OrderDetailsPage/logic'
import { salesOrder_salesOrder_salesOrderLines_items } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'
import { HTS_TAB_DETAILS } from '@Orders/constants'
import HTSSection from '@Orders/OrderDetailsPage/HTS/HTSSection'
import {
    TYPE_OF_HTS,
    TITLE_OF_HTS_TYPE,
    setLocalStorageItem,
    getLocalStorageItem,
} from '@Orders/OrderDetailsPage/HTS/HTSLogic'
import styles from '@Orders/OrderDetailsPage/styles.css'
import { MoreInformationText } from '@Orders/OrderDetailsPage/HTS/modals'
import { useDispatch } from 'react-redux'
import moment from 'moment-timezone'

import Link from '~/core/Link'
import Box from '~/core/Box'
import Text from '~/core/Text'
import { centsToDollarsNumber } from '~/lib/centsToDollars'
import { openChatDriftHTSCodes } from '~/redux-modules/drift'

import { useGetTotalOverseasAmount } from './logic'

interface CustomerClearanceInfoPropsType {
    orderId: string
    orderName: string
    checked5106: boolean
    onCheckboxChange: Function
    openChat?: Function
    isAllHtsValuesSubmitted?: boolean
    salesOrderLines: salesOrder_salesOrder_salesOrderLines_items[]
    isLocatedInAdminPage?: boolean
    isInternational?: boolean
    userId?: number
    htsType: string
    changeHTSType: (htsType: string) => void
    isImOrder: boolean
    createdAt: string
    isShipmentDone: boolean
}

const shouldCustomClearanceBeDisabled = (createdAt: string) => {
    return moment() > moment(createdAt).add(2, 'days')
}

export default function CustomerClearanceInfo(
    props: CustomerClearanceInfoPropsType,
) {
    const {
        changeHTSType,
        checked5106,
        salesOrderLines,
        htsType,
        isAllHtsValuesSubmitted,
        isInternational,
        isLocatedInAdminPage,
        orderId,
        onCheckboxChange,
        orderName,
        userId,
        isImOrder,
        createdAt,
        isShipmentDone,
    } = props
    const dispatch = useDispatch()

    const orderLines = isImOrder
        ? salesOrderLines.filter(
              item => item.processName !== 'Injection Molding Tooling',
          )
        : salesOrderLines

    const isAnylineWithProcessName = salesOrderLines.some(
        salesOrderLine => salesOrderLine.processName,
    )

    const lines =
        isAnylineWithProcessName && isImOrder ? orderLines : salesOrderLines

    const totalOverseasAmount = useGetTotalOverseasAmount(
        lines,
        isInternational,
    )

    const submittedHtsType =
        lines &&
        lines[0]?.htsUsageType &&
        TITLE_OF_HTS_TYPE[lines[0]?.htsUsageType]
    const totalOverseasAmountInDollars = Number(
        centsToDollarsNumber(totalOverseasAmount),
    )
    const isHTSSubmissionTimeExpired =
        shouldCustomClearanceBeDisabled(createdAt)
    const tcsMailLink = 'mailto:tcs@fictiv.com?subject='.concat(
        `Customs information for ${orderName}`,
    )

    const htsUsageTypes = lines?.map(line => line.htsUsageType)

    const areHtsValuesSubmitted = htsUsageTypes?.every(
        htsUsageType => htsUsageType,
    )

    let htsUsageType

    if (areHtsValuesSubmitted) {
        htsUsageType = htsUsageTypes?.some(
            usageType => usageType === TYPE_OF_HTS.COMMERCIAL,
        )
            ? TYPE_OF_HTS.COMMERCIAL
            : TYPE_OF_HTS.PROTOTYPING
    } else {
        htsUsageType = TYPE_OF_HTS.PROTOTYPING
    }

    if (htsUsageType) {
        setLocalStorageItem(`${HTS_TYPE}:${orderName}`, htsUsageType)
    }
    const defaultRadioValue = getLocalStorageItem(`${HTS_TYPE}:${orderName}`)

    const onRadioButtonChange = (event: RadioChangeEvent) => {
        if (event.target.value === TYPE_OF_HTS.PROTOTYPING) {
            setLocalStorageItem(
                `${HTS_TYPE}:${orderName}`,
                TYPE_OF_HTS.PROTOTYPING,
            )
            changeHTSType(TYPE_OF_HTS.PROTOTYPING)
        } else {
            setLocalStorageItem(
                `${HTS_TYPE}:${orderName}`,
                TYPE_OF_HTS.COMMERCIAL,
            )
            changeHTSType(TYPE_OF_HTS.COMMERCIAL)
        }
    }

    return (
        <Box
            backgroundColor="white"
            borderRadius="4px"
            className={styles.borderForClearanceInfo}
            direction="col"
            padding="24px"
        >
            <Box direction="col" className={styles.htsDdpInnerCard}>
                <Box
                    display="flex"
                    borderBottom="1px solid rgba(0, 0, 0, 0.15)"
                >
                    <Box
                        flex={1}
                        direction="col"
                        className={styles.htsHeader}
                        data-test-target="required-custom-info"
                    >
                        Required customs information
                        <Box>
                            <Text
                                fontWeight={700}
                                fontSize="20px"
                                color="blackAlpha85"
                                data-test-target="required-custom-info"
                            >
                                Complete the step(s) below
                            </Text>
                            {isAllHtsValuesSubmitted && (
                                <Tag
                                    color="green"
                                    style={{
                                        cursor: 'default',
                                        margin: '12px 0px 0px 8px',
                                        height: '20px',
                                        width: 'fit-content',
                                        fontWeight: 400,
                                    }}
                                >
                                    Submitted
                                </Tag>
                            )}
                        </Box>
                    </Box>
                    <Box width="300px" style={{ flexShrink: 0 }}>
                        <MoreInformationText openChat={openChatDriftHTSCodes} />
                    </Box>
                </Box>
                {isAllHtsValuesSubmitted && (
                    <Alert
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined />}
                        style={{ margin: '16px 0px' }}
                        message={`Customs clearance information submitted successfully for ${submittedHtsType} use.`}
                        description={
                            <>
                                Please stay tuned for any followups from Fictiv
                                or FedEx to prevent order delays, since
                                additional information may be requested at the
                                point of importation.
                                <br />
                                Meanwhile, please{' '}
                                <a
                                    onClick={e => {
                                        e.preventDefault()
                                        if (props.openChat) {
                                            props.openChat()
                                        }
                                    }}
                                >
                                    contact us
                                </a>{' '}
                                if you have any further questions, concerns, or
                                need to make changes.
                            </>
                        }
                    />
                )}
                <>
                    {!isAllHtsValuesSubmitted &&
                        isHTSSubmissionTimeExpired &&
                        isShipmentDone && (
                            <Alert
                                className={styles.alertWarningStyle}
                                style={{
                                    marginTop: '15px',
                                }}
                                type="warning"
                                showIcon
                                description={
                                    <>
                                        In an effort to get your parts shipped
                                        in a timely manner, Fictiv has attempted
                                        to provide this information on your
                                        behalf; however, there's still a risk of
                                        delay if the information is not
                                        accurate. To avoid potential delays on
                                        future orders, please provide the
                                        customs information for overseas
                                        production within 48 hours of placing an
                                        order. If you have any other questions,
                                        please{' '}
                                        <Link
                                            onClick={(event: Event) => {
                                                event.preventDefault()
                                                dispatch(
                                                    openChatDriftHTSCodes(),
                                                )
                                            }}
                                        >
                                            chat with us
                                        </Link>{' '}
                                        or send an email to{' '}
                                        <a
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            href={tcsMailLink}
                                        >
                                            tcs@fictiv.com
                                        </a>
                                        .
                                    </>
                                }
                                message={
                                    'Parts in this order have been shipped and customs information can no longer be edited.'
                                }
                            />
                        )}
                    {isHTSSubmissionTimeExpired &&
                        !isAllHtsValuesSubmitted &&
                        !isShipmentDone && (
                            <Alert
                                className={styles.alertWarningStyle}
                                type="warning"
                                style={{
                                    marginTop: '15px',
                                }}
                                showIcon
                                description={
                                    <>
                                        The 48 hour window to provide customs
                                        information has passed and this form is
                                        no longer editable directly. In an
                                        effort to get your parts delivered in a
                                        timely manner, Fictiv has attempted to
                                        provide this information on your behalf;
                                        however, there's still a risk of a delay
                                        if the information is not accurate.
                                        Please{' '}
                                        <Link
                                            onClick={(event: Event) => {
                                                event.preventDefault()
                                                dispatch(
                                                    openChatDriftHTSCodes(),
                                                )
                                            }}
                                        >
                                            chat with us
                                        </Link>{' '}
                                        or send an email to{' '}
                                        <a
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            href={tcsMailLink}
                                        >
                                            tcs@fictiv.com
                                        </a>{' '}
                                        as soon as possible if you see any
                                        errors as this information cannot be
                                        modified after parts have been shipped.
                                    </>
                                }
                                message={
                                    'Please review the following customs information and contact us if you need to update the information.'
                                }
                            />
                        )}
                    <Box
                        className={
                            isAllHtsValuesSubmitted
                                ? styles.htsSubHeaderNoPadding
                                : styles.htsSubHeader
                        }
                    >
                        1. Are the parts in this order used exclusively for
                        development, testing, product evaluation, or quality
                        control purposes, and are the parts NOT intended to be
                        sold, or incorporated into other products that are sold?
                    </Box>
                    <Box className={styles.htsSubHeaderDescription}>
                        Prototypes used exclusively for product development and
                        testing may qualify for a duty-free claim under U.S.
                        import regulations. By selecting “Yes,” you are willing
                        and able to provide evidence to support the duty-free
                        claim should U.S. Customs request additional
                        information.
                    </Box>
                    <Box
                        placement={HTS_TAB_DETAILS}
                        className={styles.partsButton}
                    >
                        <Radio.Group
                            disabled={
                                isAllHtsValuesSubmitted ||
                                isHTSSubmissionTimeExpired
                            }
                            onChange={onRadioButtonChange}
                            defaultValue={
                                htsUsageType ? htsUsageType : defaultRadioValue
                            }
                        >
                            <Radio value={TYPE_OF_HTS.PROTOTYPING}>
                                Yes (prototype)
                            </Radio>
                            <Radio value={TYPE_OF_HTS.COMMERCIAL}>
                                No (commercial)
                            </Radio>
                        </Radio.Group>
                    </Box>
                </>
            </Box>
            <Box direction="col" marginLeft="0px">
                <HTSSection
                    orderId={orderId}
                    orderName={orderName}
                    checked5106={checked5106}
                    onCheckboxChange={onCheckboxChange}
                    typeOfHts={htsType}
                    isAllHtsValuesSubmitted={isAllHtsValuesSubmitted}
                    salesOrderLines={orderLines}
                    totalOverseasAmountInDollars={totalOverseasAmountInDollars}
                    isLocatedInAdminPage={isLocatedInAdminPage}
                    userId={userId}
                    isAnylineWithProcessName={isAnylineWithProcessName}
                    isImOrder={isImOrder}
                    isHTSSubmissionTimeExpired={isHTSSubmissionTimeExpired}
                />
            </Box>
        </Box>
    )
}
